import ScrollingLogos from "./ScrollingLogos";
import { Box, Typography } from "@mui/material";

// Figma: SaaS Homepage, 510:14995.
const STEPS = [
  {
    number: "01",
    title: "Completed in 12-15 days",
    body: "Brief to delivery. Creators already on board, with no rate negotiations.",
  },
  {
    number: "02",
    title: "Clear pricing upfront",
    body: "Per piece or per creator. Rates agreed before your campaign starts.",
  },
  {
    number: "03",
    title: "Tracked to the sale",
    body: "Views, clicks, traffic and purchases, by creator, as your campaign runs.",
  },
];

const TRUSTED_BY_STATS = [
  { value: "250K+", label: "Active Creators" },
  { value: "1000+", label: "Campaigns Running" },
];

const CARD_BG = "#fce2dd";
const BRAND = "#ee4d37";
const SECONDARY_TEXT = "#8a8a8a";

const SECTION_LABEL_SX = {
  mb: "8px",
  fontSize: 14,
  lineHeight: "20px",
  fontWeight: 600,
  textTransform: "uppercase",
};

// Short windows (a laptop with browser chrome) get smaller stat cards, so nothing is cut off.
const SHORT_WINDOW = "@media (max-height: 780px)";

const STAT_CARD_SX = {
  height: "100px",
  overflow: "hidden",
  borderRadius: "12px",
  background: "#ffffff",
  p: 2,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  [SHORT_WINDOW]: { height: "76px", p: "12px 16px" },
};

const STAT_VALUE_SX = {
  m: 0,
  color: BRAND,
  fontSize: 32,
  lineHeight: "40px",
  fontWeight: 600,
  letterSpacing: "0.2px",
  [SHORT_WINDOW]: { fontSize: 28, lineHeight: "32px" },
};

const STAT_LABEL_SX = {
  m: 0,
  color: SECONDARY_TEXT,
  // Scales with the panel (which is 45% of the window), so labels stay on one line and the card keeps
  // the design's 100px height.
  fontSize: "clamp(12px, 1vw, 16px)",
  lineHeight: "24px",
  fontWeight: 500,
  whiteSpace: "nowrap",
  [SHORT_WINDOW]: { lineHeight: "20px" },
};

// Gaps that shrink with the window height, so the whole panel fits without scrolling.
const SECTION_GAP = "clamp(16px, 3vh, 40px)";

/**
 * The branding panel beside the auth forms.
 *
 * fitHeight: fill the height it is given, never scroll. The brands widget takes all the room that
 * is left, so it grows with the window and "Trusted by" stays at the bottom; the gaps shrink, so it
 * fits any window from about 650px tall. Without it (the stacked mobile layout) the panel is its
 * natural height, with a 320px widget.
 */
export default function RightBrandingPanel({ fitHeight = false }) {
  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "16px",
        background: BRAND,
        p: "24px",
        boxSizing: "border-box",
        color: "#ffffff",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box sx={{ pt: fitHeight ? "clamp(16px, 6vh, 48px)" : "48px" }}>
        <Typography
          fontFamily="Lexend"
          sx={{
            fontSize: 48,
            lineHeight: "56px",
            fontWeight: 600,
            letterSpacing: "0.2px",
          }}
        >
          WishlinkOS
        </Typography>
        <Typography
          fontFamily="Lexend"
          sx={{
            mt: "8px",
            fontSize: 16,
            lineHeight: "24px",
            fontWeight: 500,
            letterSpacing: "0.2px",
          }}
        >
          Influencer Marketing you can plan, price and prove
        </Typography>
      </Box>

      {/* A small gap, so the steps sit close under the header and the logos get the spare height. */}
      <Box
        sx={
          fitHeight
            ? {
                height: "clamp(16px, 6vh, 56px)",
                flexShrink: 1,
                minHeight: "8px",
              }
            : { height: "56px", flexShrink: 0 }
        }
      />

      <Box sx={{ flexShrink: 0 }}>
        <Typography fontFamily="Lexend" sx={SECTION_LABEL_SX}>
          How does it work?
        </Typography>
        <Box
          sx={{
            background: CARD_BG,
            borderRadius: "12px",
            p: 2,
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 3,
          }}
        >
          {STEPS.map((step) => (
            <Box key={step.number}>
              <Typography
                fontFamily="Lexend"
                sx={{
                  mb: "8px",
                  color: BRAND,
                  fontSize: 16,
                  lineHeight: "20px",
                  fontWeight: 500,
                }}
              >
                {step.number}
              </Typography>
              <Typography
                fontFamily="Lexend"
                sx={{
                  mb: "4px",
                  color: "#0d0d0d",
                  fontSize: 14,
                  lineHeight: "20px",
                  fontWeight: 500,
                }}
              >
                {step.title}
              </Typography>
              <Typography
                fontFamily="Lexend"
                sx={{
                  color: SECONDARY_TEXT,
                  fontSize: 12,
                  lineHeight: "16px",
                  fontWeight: 400,
                }}
              >
                {step.body}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          mt: SECTION_GAP,
          position: "relative",
          borderRadius: "8px",
          overflow: "hidden",
          ...(fitHeight
            ? { flex: "1 1 0", minHeight: "72px" }
            : { height: "320px", flexShrink: 0 }),
        }}
      >
        <ScrollingLogos gradientColor={BRAND} variant="authPanel" />
      </Box>

      <Box sx={{ mt: SECTION_GAP, flexShrink: 0 }}>
        <Typography fontFamily="Lexend" sx={SECTION_LABEL_SX}>
          Trusted by
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 2,
          }}
        >
          {TRUSTED_BY_STATS.map((item) => (
            <Box key={item.label} sx={STAT_CARD_SX}>
              <Typography fontFamily="Lexend" sx={STAT_VALUE_SX}>
                {item.value}
              </Typography>
              <Typography fontFamily="Lexend" sx={STAT_LABEL_SX}>
                {item.label}
              </Typography>
            </Box>
          ))}
          <Box
            sx={{
              ...STAT_CARD_SX,
              p: 0,
              [SHORT_WINDOW]: { height: "76px" },
            }}
          >
            <Box
              component="img"
              src="https://images.wishlink.com/alabaster/MetaBusinessPartner.png"
              alt="Meta Business Partner"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                // Hide the badge's own dark outline at the sides; the white card shows through.
                clipPath: "inset(0 1.5%)",
                display: "block",
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
